import { useState, useContext } from 'react';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { getItemProperties } from '../../../contexts/MixpanelHelper';
import user from '../../../helpers/user';
import { __ } from '../../../translations';
import AlternativePaymentLinks from './AlternativePaymentLinks';
import { TVODOverlayType } from './TVODOverlay';
import useBraze from '../../../hooks/useBraze';
import { BRAZE } from '../../../config/analytics-events';
import { log } from '@intigral/utils';
import { tvodOneTimePurchaseApi } from '../../../services/serviceWrapper/tvodServiceWrapper';
import { QitafDiscountBreakdown } from './QitafDiscountBreakdown';
import { FlexWrapper, Text, Button, FontIcon, PasswordInputField } from '@intigral/shared';
import withPresetWidth from '@intigral/shared/components/hocs/withPresetWidth/withPresetWidth';
import { ppvOneTimePurchaseApi } from '../../../services/serviceWrapper/ppvServiceWrapper';
import AlternativePaymentLinksPPV from './AlternativePaymentLinksPPV';
import { EVENT } from '../../../contexts/MixpanelConstants';
import { useToggleWatchlistItem } from '../../../hooks/watchlist/useToggleWatchlist';

interface tvodInfo {
    priceWithVat: number;
    currency: string;
    productId: string;
}

interface InputPasswordPropsType {
    tvodInfo?: tvodInfo;
    setCurrentComponentType: (e: any) => void;
    content: any;
    isPPV?: boolean;
    eventData?: any;
    handlePurchase: (e: any) => void;
    onClose: () => void;
    qitafUpdatedPrice?: number;
    qitafApplied?: boolean;
}

const InputPassword = ({
    tvodInfo,
    setCurrentComponentType,
    content,
    isPPV,
    eventData,
    handlePurchase,
    onClose,
    qitafUpdatedPrice,
    qitafApplied = false,
}: InputPasswordPropsType) => {
    const { logBrazeCustomEvent } = useBraze();
    const username = user.getMSISDN() || user.getUsername();
    const durationInHours = 48;
    const { priceWithVat, currency, productId } = tvodInfo || {};
    const [password, setPassword] = useState(null);
    const toggleWatchlistItemMutation = useToggleWatchlistItem();

    const displayPrice =
        qitafApplied && qitafUpdatedPrice != null ? Number(qitafUpdatedPrice).toFixed(2) : priceWithVat;

    let tvodDescriptionOneText = __('tvod_description_one') || '';
    let tvodDescriptionTwoText = __('tvod_description_two') || '';

    tvodDescriptionOneText = tvodDescriptionOneText.replace('{username}', username);
    tvodDescriptionOneText = tvodDescriptionOneText.replace('{priceWithVat}', displayPrice);
    tvodDescriptionOneText = tvodDescriptionOneText.replace('{currency}', currency);
    tvodDescriptionTwoText = tvodDescriptionTwoText.replace('{durationInHours}', durationInHours);

    let tvodButtonText = __('tvod_pay_button') || '';
    tvodButtonText = tvodButtonText.replace('{priceWithVat}', priceWithVat.toString());
    tvodButtonText = tvodButtonText.replace('{currency}', currency);
    tvodButtonText = tvodButtonText.replace('{durationInHours}', durationInHours.toString());

    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [showLoader, setShowLoader] = useState(false);

    const { track } = useContext(MixpanelContext);

    const onPaySelected = async () => {
        setButtonDisabled(true);
        setShowLoader(true);
        const userInputPassword = password;
        if (isPPV) {
            track(EVENT.PPV.PPV_PURCHASED_STARTED, {
                'Event Title': eventData?.name,
                'Event ID': eventData?.eventId,
                'Purchase Method': 'STCH',
            });
            try {
                const oneTimePurchase = await ppvOneTimePurchaseApi({
                    eventData,
                    pin: userInputPassword,
                    handlePurchase,
                });
                if (oneTimePurchase === 'accepted') {
                    onClose();
                    track(EVENT.PPV.PPV_PURCHASED_COMPLETED, {
                        'Event Title': eventData?.name,
                        'Event ID': eventData?.eventId,
                        'Purchase Method': 'STCH',
                        Platform: 'Web',
                    });
                    return false;
                }
            } catch (e) {
                track(EVENT.PPV.PPV_PURCHASED_FAILED, {
                    'Failure Reason': JSON.stringify(e),
                });
            }
        } else {
            try {
                track(`TVOD - Purchase - Attempt`, {});
                const oneTimePurchase = await tvodOneTimePurchaseApi(
                    {
                        productId,
                        password: userInputPassword,
                        currency,
                    },
                    handlePurchase
                );
                if (oneTimePurchase) {
                    setCurrentComponentType(TVODOverlayType.SUCCESS);
                    if (content?.Id) {
                        toggleWatchlistItemMutation.mutate({
                            itemId: content.Id,
                            category: (content?.category?.toLowerCase() ?? '') as 'linear' | 'vod',
                            isAdded: false,
                        });
                    }
                    track(`TVOD - Purchase - Success`, getItemProperties(content));
                    logBrazeCustomEvent(BRAZE.EVENTS.TVOD_PURCHASE, {
                        [BRAZE.PROPERTIES.MOVIE_NAME]: content?.textEn ?? '',
                        [BRAZE.PROPERTIES.GENRE]: content?.genre ?? '',
                        [BRAZE.PROPERTIES.LANGUAGE]:
                            (typeof content?.language === 'string' ? content.language : content.language.join(',')) ??
                            '',
                        [BRAZE.PROPERTIES.PRICE]: priceWithVat ?? '',
                    });
                    return false;
                } else {
                    setButtonDisabled(false);
                }
            } catch (e) {
                setButtonDisabled(false);
                setShowLoader(false);
                track(`TVOD - Purchase - Failed`, {
                    ...getItemProperties(content),
                    'Failure Reason': JSON.stringify(e),
                });
            }
        }
        setShowLoader(false);
    };
    const onInputChange = (e) => {
        setPassword(e.target.value);
        if (e.target.value.length < 6) {
            setButtonDisabled(true);
        } else {
            setButtonDisabled(false);
        }
    };

    const FlexWidth = withPresetWidth(FlexWrapper);

    return (
        <>
            <FlexWrapper flexDirection="column" gap={{ default: '2x', md: '3x' }}>
                <FontIcon name="cart" size="large" md="size60" color="green" />
                <Text as="h2" variant="h2" margin="0">
                    {isPPV ? __('tvod_ppv_rent_now') : __('tvod_rent_now')}
                </Text>

                {qitafApplied && qitafUpdatedPrice != null && qitafUpdatedPrice !== priceWithVat && (
                    <QitafDiscountBreakdown priceWithVat={priceWithVat} updatedPrice={qitafUpdatedPrice} />
                )}

                <Text as="p" margin="0" color="gray" size="14">
                    <span dangerouslySetInnerHTML={{ __html: tvodDescriptionOneText }} />
                </Text>
                <Text as="p" margin="0" color="gray" size="14">
                    <span dangerouslySetInnerHTML={{ __html: tvodDescriptionTwoText }} />
                </Text>
                <Text as="h4" variant="h4" margin="0">
                    {__('tvod_enter_password')}
                </Text>
                <FlexWidth flexDirection="column" gap={{ default: '2x', md: '3x' }} presetWidth="medium">
                    <PasswordInputField
                        required
                        pattern="[a-z0-9]{6,99}$"
                        label={__('tvod_password')}
                        onChange={onInputChange}
                        value={password}
                        autoFocus
                    />
                    <Button
                        onClick={onPaySelected}
                        {...(buttonDisabled && { disabled: true })}
                        {...(showLoader && { loading: true })}
                    >
                        {tvodButtonText}{' '}
                        {qitafApplied && qitafUpdatedPrice != null ? Number(qitafUpdatedPrice).toFixed(2) : ''}{' '}
                        {qitafApplied ? __('qitaf_currency') : ''}
                    </Button>
                </FlexWidth>
                {isPPV ? (
                    <AlternativePaymentLinksPPV
                        onSelected={setCurrentComponentType}
                        currentOverlayType={TVODOverlayType.PASSWORD}
                    />
                ) : (
                    <AlternativePaymentLinks
                        onSelected={setCurrentComponentType}
                        currentOverlayType={TVODOverlayType.PASSWORD}
                    />
                )}
            </FlexWrapper>
        </>
    );
};

export default InputPassword;
